import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useSession } from '../../api';
import { useReporter } from '../../reporter';
import { useLocalStorage } from '../../hooks';
import {
  useCart,
  useCartLineItemForPet,
  useSessionCustomer,
} from '../../customer';
import { usePetWithLocalFallback, usePets } from '../../pets';
import { useOffer } from '../../offer';
import { useProduct, useProductRecommendations } from '../../products';
import { useSupplements, useTreats } from '../../addons';
import { useToast } from '../../Toast';
import { CHECKOUT_SUBPATH, QUIZ_SUBPATH, USER_INFO_SLUG } from '../routing';
import {
  useExperiment,
  SHOW_HALF_FRESH,
  DEFAULT_CADENCE_ONBOARDING,
  SHORTENED_ONBOARDING_QUIZ,
  EXTRAS_PAGE_CRO,
  TURKEY_DEFAULT_RECIPE,
  SHOW_ONBOARDING_INTERSTITIAL,
  ONBOARDING_QUIZ_HAPPINESS_REDESIGN,
  SHOW_DISCOUNT_BANNER_ONBOARDING_QUIZ,
} from '../../experiments';
import {
  useMachine,
  onboardingMachine,
  shortenedOnboardingMachine,
} from './state';
import { isCompletePet } from './guard';
import {
  STATE,
  ACTIONS,
  PLAN_SELECTION_KEY,
  ONBOARDING_VARIATIONS,
  BEST_SELLER_SKUS,
  RECIPE_VARIATIONS,
} from './constants';
import {
  getCartProduct,
  getPlanTypeFromRecipes,
  getQuizStepNumbers,
} from './functions';

const initialContext = {};
export const OnboardingContext = createContext(initialContext);

export const OnboardingProvider = ({ children }) => {
  const router = useRouter();
  const reporter = useReporter();
  const [showModal, setShowModal] = useState(false);
  const [isReturningCustomerWithMealPlan, setReturningCustomerWithMealPlan] =
    useState(null);
  const [localSelection, setLocalSelection] =
    useLocalStorage(PLAN_SELECTION_KEY);

  const { status, isAppSession, isScreeningSession, isRegistering } =
    useSession();

  let { value: defaultCadenceOnboarding } = useExperiment(
    DEFAULT_CADENCE_ONBOARDING,
    false,
  );

  const { value: showDiscountBanner } = useExperiment(
    SHOW_DISCOUNT_BANNER_ONBOARDING_QUIZ,
    false,
  );

  const { value: happinessOnboardingQuiz } = useExperiment(
    ONBOARDING_QUIZ_HAPPINESS_REDESIGN,
    'control',
  );

  const { value: showHalfFresh } = useExperiment(SHOW_HALF_FRESH, false);

  const { value: defaultTurkeyRecipe } = useExperiment(
    TURKEY_DEFAULT_RECIPE,
    RECIPE_VARIATIONS.control,
  );

  const { value: extrasPageCRO } = useExperiment(EXTRAS_PAGE_CRO, 'control');

  const { value: showOnboardingInterstitial } = useExperiment(
    SHOW_ONBOARDING_INTERSTITIAL,
    false,
  );

  const { value: shortenedOnboardingQuiz } = useExperiment(
    SHORTENED_ONBOARDING_QUIZ,
    'control',
  );

  const isSessionLoading = status === 'loading';
  const {
    customer,
    isActive,
    isLoading: customerIsLoading,
    isAuthenticated,
    updateCustomer,
    getCustomer,
  } = useSessionCustomer();

  const { pets, isLoading: petsIsLoading, ...petsApi } = usePets();
  const { cart, isLoading: cartIsLoading } = useCart();
  const { showToast } = useToast();
  const offer = useOffer();

  const currentMachine =
    shortenedOnboardingQuiz !== ONBOARDING_VARIATIONS.control
      ? shortenedOnboardingMachine
      : onboardingMachine;
  const [state, send, service] = useMachine(currentMachine);

  const pet_id =
    state.context.pet_id || state.context.pet?.id || router.query?.pet_id;
  const isNewPet = pet_id === 'new_pet';
  const {
    pet,
    isLoading: petIsLoading,
    savePet,
    deletePet,
    isLocalPet,
    deleteLocalPet,
  } = usePetWithLocalFallback(!isNewPet ? pet_id : undefined);

  const { products: treatProducts, isLoading: isTreatsLoading } = useTreats({
    petId: pet_id,
    couponCode: offer.code,
  });

  const { products: supplementProducts, isLoading: isSupplementsLoading } =
    useSupplements({ petId: pet_id, couponCode: offer.code });

  const plansApi = useProductRecommendations({
    couponCode: offer.code,
    petId: isCompletePet(pet) ? pet_id : null,
  });

  const planStates = [
    STATE.planSelection,
    STATE.fresh,
    STATE.baked,
    STATE.recommendedPlan,
  ];

  const postMealPrepPlanStates = [STATE.fresh, STATE.baked];

  const fromRecommendedPlan =
    state.context.stateKey === STATE.planSelection &&
    state.context.prevState.stateKey === STATE.recommendedPlan;
  const isValidating =
    !planStates.includes(state.name) ||
    plansApi.isLoading ||
    plansApi.isValidating;
  //^^ only want isValidating to return false on plan selection steps
  // this solves: plansApi.isValidating will return false before it
  // returns true when plans refresh, allowing no recipes found screen to flash

  useEffect(() => {
    // so productTypes are not stale
    if (postMealPrepPlanStates.includes(state.name)) {
      if (!fromRecommendedPlan) {
        plansApi.refreshPlans();
      }
    }

    // disabling warning because we don't want useEffect to watch plansApi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.name]);

  const { fresh, dry, mixed, half_fresh } = plansApi;

  const mixedCaloriesPerDay = mixed?.products?.[0]?.kcalories_per_day ?? null; // won't be available if a pup is allergic to ingredients in a mixed plan
  const productTypes = { fresh, dry, mixed };
  if (showHalfFresh) productTypes.half_fresh = half_fresh;

  const { lineItemForPet, addonsForPet, ...cartApi } =
    useCartLineItemForPet(pet_id);

  const productApi = useProduct(lineItemForPet?.sku, {
    couponCode: offer.code,
  });

  let cartProduct;
  if (!state.context.stateKey.includes(QUIZ_SUBPATH)) {
    cartProduct = getCartProduct(productApi.product, lineItemForPet);
  }

  const isLoading =
    isSessionLoading ||
    (isAuthenticated &&
      (customerIsLoading ||
        petsIsLoading ||
        cartIsLoading ||
        isTreatsLoading ||
        isSupplementsLoading));

  const goToLogin = () => {
    router.push(`/auth/login/`);
    showToast({
      status: 'success',
      message:
        'We found an account associated with your email. Please log in to access your account.',
    });
  };

  const goBack = () => {
    send(ACTIONS.goBack());
  };

  const returnToQuiz = slug => {
    // Handling when the function is called by element onClick handler which
    // passes an event object. We are only interested in this argument if its a string.
    if (typeof slug !== 'string') slug = USER_INFO_SLUG;

    router.push(`/onboarding/quiz/${slug}/?pet_id=${pet_id}`);
  };

  const onClickDeletePet = async () => {
    try {
      await deletePet();
      router.push(`/onboarding/${CHECKOUT_SUBPATH}/`);
    } catch (e) {
      reporter.error(e);
    }
  };

  useEffect(() => {
    // Doing this to only add the values we need to localStorage, rather than the entire
    // onboarding context.
    const valuesToStore = {
      addons: state.context.addons,
      customer: state.context.customer,
      nextKey: state.context.nextKey,
      pet: state.context.pet,
      pet_id: state.context.pet_id,
      planType: state.context.planType,
      recipes: state.context.recipes,
      stateKey: state.context.stateKey,
    };
    setLocalSelection(valuesToStore, {
      skipRender: true,
    });
  }, [state.context, setLocalSelection]);

  useEffect(() => {
    if (!isLoading && state.name === STATE.initial && !isRegistering) {
      let recipes;
      let planType;
      if (!petIsLoading && !productApi.isLoading) {
        const cartProduct = getCartProduct(productApi.product, lineItemForPet);

        if (cartProduct) {
          recipes = cartProduct.variants[0].recipes;
          const portionSize = cartProduct.portion_size;
          planType = getPlanTypeFromRecipes(recipes, portionSize);
        } else if (localSelection) {
          recipes = localSelection.recipes;
          planType = localSelection.planType;
        }
      }

      send(
        ACTIONS.initialLoad({
          customer,
          isActive,
          pet,
          pets: isLocalPet ? [pet] : pets,
          pet_id,
          cart,
          recipes,
          planType,
          isAppSession,
          isScreeningSession,
          flags: {
            defaultCadenceOnboarding,
            shortenedOnboardingQuiz,
            extrasPageCRO,
            showOnboardingInterstitial,
            happinessOnboardingQuiz,
            showDiscountBanner,
          },
        }),
      );
    }
  }, [
    state,
    send,
    cart,
    customer,
    isActive,
    pet,
    pets,
    pet_id,
    isLoading,
    petIsLoading,
    productApi.isLoading,
    lineItemForPet,
    localSelection,
    productApi.product,
    cartApi,
    extrasPageCRO,
    shortenedOnboardingQuiz,
    happinessOnboardingQuiz,
    showOnboardingInterstitial,
    showDiscountBanner,
    treatProducts,
    supplementProducts,
    isLocalPet,
    isAppSession,
    isRegistering,
    isScreeningSession,
    defaultCadenceOnboarding,
  ]);

  const filterBestSellers = (products, SKUs) => {
    return products?.filter(product => SKUs.includes(product.sku));
  };

  const planSelectionProps = {
    productTypes,
    localSelection,
    setLocalSelection,
    offer,
    isValidating,
    productIsLoading: productApi.isLoading,
    returnToQuiz,
    planApiError: plansApi.error,
    onClickDeletePet,
    cartProduct,
    plansApi,
  };

  const values = {
    ...planSelectionProps,
    petIsLoading,
    pets,
    pet,
    savePet,
    deletePet,
    cart,
    customer,
    isActive,
    getCustomer,
    updateCustomer,
    isLoading,
    send,
    service,
    goToLogin,
    pet_id: state.context.pet_id,
    nextKey: state.context.nextKey,
    prevState: state.context.prevState,
    stateKey: state.name,
    selectedPlanType: state.context.planType,
    selectedFreshRecipes: state.context.recipes.fresh,
    selectedDryRecipes: state.context.recipes.dry,
    reporter,
    showModal,
    setShowModal,
    goBack,
    deleteLocalPet,
    pricePeriod: 'week',
    cartApi,
    petsApi,
    treatProducts:
      extrasPageCRO === 'best_sellers'
        ? filterBestSellers(treatProducts, BEST_SELLER_SKUS)
        : treatProducts,
    supplementProducts:
      extrasPageCRO === 'best_sellers'
        ? filterBestSellers(supplementProducts, BEST_SELLER_SKUS)
        : supplementProducts,
    selectedAddons: addonsForPet,
    selectedExtras: addonsForPet ?? state.context.addons,
    quizStepNumbers: getQuizStepNumbers(shortenedOnboardingQuiz),
    isLocalPet,
    mixedCaloriesPerDay,
    isAppSession,
    isScreeningSession,
    flags: {
      defaultCadenceOnboarding,
      shortenedOnboardingQuiz,
      extrasPageCRO,
      defaultTurkeyRecipe,
      showOnboardingInterstitial,
      happinessOnboardingQuiz,
      showDiscountBanner,
    },
    isReturningCustomerWithMealPlan,
    setReturningCustomerWithMealPlan,
  };

  return (
    <OnboardingContext.Provider value={values}>
      {children}
    </OnboardingContext.Provider>
  );
};

OnboardingProvider.propTypes = {
  children: PropTypes.object.isRequired,
};
